import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  caisseBridgeLink,
  useGetSessionDetailQuery,
  usePrintCapabilitiesQuery,
  usePrintSessionTotalMutation,
  useLazyPrintTotalStatusQuery,
  useHandoffSessionToCaisseMutation,
  useFinishSessionMutation,
  useCancelSessionMutation,
  useCloseSessionMutation,
} from '@/features/waiter/waiterApi';
import { useWaiterAuthGuard } from '@/features/waiter/useWaiterAuthGuard';
// SC-960 — table des messages et sondage partagés avec la modale « Commande en
// ligne », qui a reçu le même « Imprimer ». Ce fichier en portait la copie que
// son propre commentaire signalait comme dette ; il ne pouvait pas y en avoir
// une troisième.
import {
  printOutcomeDestination,
  PRINT_OUTCOME_MESSAGES,
  reportPrintOutcome,
} from '@/features/waiter/printOutcome';
import { formatPrice } from '@/components/ui/Price';
import { useModalBackClose } from './useModalBackClose';
import { useScrimDismiss } from './useScrimDismiss';
import KitchenReprintSheet from './KitchenReprintSheet';
import KitchenIcon from './KitchenIcon';
import type { ShowWaiterToast } from './waiterToast';

export interface TicketFooterProps {
  sessionUuid: string;
  /** DF-0349 — ce que la feuille du ✔ nomme : la table (« 23 », « 20+21+22 »,
   *  l'étiquette de tablée de l'en-tête) ou la note Emporter (« Paul »). */
  kind: 'dine_in' | 'takeaway';
  label: string;
  /** DF-0193 — le garde 401/403 en a besoin pour vider l'auth du bon restaurant. */
  restaurantUuid: string;
  /** Waiter JWT — every call here uses it. */
  token: string;
  /** A non-empty waiter cart (unsent to the kitchen) makes Partir go through
   *  the EXISTING ← back leave-guard first (W-T-85) before this component's
   *  own `leave-confirm`. Owned by `TicketPage` (spec §1.7: one guard, one
   *  place) — this only asks for it. */
  hasUnsentCart: boolean;
  onRequestCartGuard: (onCleared: () => void) => void;
  /** finish/close/cancel all succeed the same way: leave the ticket. */
  onFinished: () => void;
  showToast: ShowWaiterToast;
  /** SC-902 — `finish`/`handoff-to-caisse` 409 `HELD_ROUND_EXISTS`: a service
   *  round is still sitting unsent. Switches the parent to the Commandes tab
   *  so the waiter lands on the `commandes-held-rounds` block/« Lancer ». */
  onShowCommandes?: () => void;
}

/**
 * SC-761 phones: the spec's fixed 104+120+52 + 3×12 gaps + 2×12 padding =
 * 336px is wider than a 320px handheld, and `.waiter-root`'s overflow:hidden
 * cut the ✔ off rather than scrolling to it — so both min-widths and the
 * wider gap only apply from 360px up.
 *
 * 04-ux-mobile-spec.md §2.7, 06-test-cases.md W-T-70..85 — Partir · Imprimer
 * · ✔, design §1.4's archive table. SC-1038 adds the kitchen-reprint button
 * right beside Imprimer. Reads its own `getSessionDetail` +
 * `printCapabilities` subscriptions (shared cache with `TicketPage` /
 * `TicketCommandes`, same idiom as those siblings).
 */
export default function TicketFooter({
  sessionUuid,
  kind,
  label,
  restaurantUuid,
  token,
  hasUnsentCart,
  onRequestCartGuard,
  onFinished,
  showToast,
  onShowCommandes,
}: TicketFooterProps) {
  const { t } = useTranslation();
  const { data: sessionDetail } = useGetSessionDetailQuery({ sessionUuid, token }, { skip: !sessionUuid || !token });
  // SC-569 — relu à chaque montage comme dans TablesOverview (le patron peut
  // lier une caisse ou brancher une imprimante cloud pendant le service), et
  // ré-interrogé lentement : le serveur peut rester des heures sur CE ticket
  // sans jamais le remonter, donc un échec passager y coûtait « Imprimer »
  // pour toute la session au lieu d'un montage.
  const { data: capabilities, error: capabilitiesError } = usePrintCapabilitiesQuery(
    { token },
    { skip: !token, refetchOnMountOrArgChange: true, pollingInterval: 60_000 },
  );
  // DF-0193 — ce sondage est le SEUL abonnement qui tourne pendant qu'un
  // serveur reste sur ce ticket : sans lecteur de 403 ici, un jeton mort ne
  // ramenait jamais au pavé PIN. `sessionDetail` ne sonde pas, son erreur est
  // néanmoins lue par le même garde côté `TicketPage`.
  useWaiterAuthGuard(restaurantUuid, [capabilitiesError]);
  const [printSessionTotal] = usePrintSessionTotalMutation();
  const [fetchPrintTotalStatus] = useLazyPrintTotalStatusQuery();
  const [handoffSessionToCaisse] = useHandoffSessionToCaisseMutation();
  const [finishSession] = useFinishSessionMutation();
  const [cancelSession] = useCancelSessionMutation();
  const [closeSession] = useCloseSessionMutation();

  const [footerBusy, setFooterBusy] = useState(false);
  const [finishSheetOpen, setFinishSheetOpen] = useState(false);
  const [finishSheetError, setFinishSheetError] = useState<string | null>(null);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);
  // DF-0223 — « Imprimer » sort l'ADDITION ; il ne met rien en cuisine. Un
  // serveur qui cherchait ou envoyer la tournee d'un client a appuye dessus
  // trois fois en une minute (Yuji, 2026-09-11, table 17) pendant que la ronde
  // dormait en file. Quand une ronde attend, le geste demande d'abord ce qu'il
  // fait.
  const [printPendingOpen, setPrintPendingOpen] = useState(false);
  // SC-1038 — la feuille « Réimprimer en cuisine ». Elle porte elle-même ses
  // appels ; le pied ne garde que l'ouverture.
  const [reprintSheetOpen, setReprintSheetOpen] = useState(false);

  useModalBackClose('finish-sheet', () => setFinishSheetOpen(false));
  useModalBackClose('leave-confirm', () => setLeaveConfirmOpen(false));
  useModalBackClose('print-pending-confirm', () => setPrintPendingOpen(false));
  const onFinishSheetScrim = useScrimDismiss(finishSheetOpen, () => setFinishSheetOpen(false), footerBusy);
  const onLeaveConfirmScrim = useScrimDismiss(leaveConfirmOpen, () => setLeaveConfirmOpen(false), footerBusy);

  const pendingRoundCount = (sessionDetail?.session.pendingValidation ?? []).length;
  const bridgeLink = caisseBridgeLink(capabilities, sessionUuid);

  // SC-1038 — fail-closed comme « Imprimer » : `capabilities` reste `undefined`
  // sur une sortie illisible, donc le bouton cuisine ne s'allume pas. Quatre
  // boutons ne tiennent pas dans les largeurs de la spec (104+120+52) sous
  // 400px — mesuré : il restait 24px libres à 360px, le bouton en demande 56.
  // Avec lui, « Partir » et « Imprimer » rendent donc 20px et 16px entre 360 et
  // 399px (et 8px de rembourrage chacun sous 360px) et retrouvent leurs
  // largeurs à 400px ; sans lui, rien ne change.
  const withKitchenReprint = Boolean(capabilities?.canReprintKitchen);

  const hasSessionLines = (sessionDetail?.orders ?? []).some((o) => o.items.length > 0);
  // D7 — the leave-confirm body states the effect with a count: only unpaid
  // orders are force-cancelled by "Partir" (design §1.4/§2.6); paid/partial
  // orders are kept, so they don't belong in this count. §2.7 — cart-option
  // question lines (`isCartOption`) are never dishes and must not inflate
  // the "n plats non payés" count.
  const unpaidLineCount = (sessionDetail?.orders ?? [])
    .filter((o) => o.onlineStatus !== 'cancelled' && o.paymentStatus !== 'paid' && o.paymentStatus !== 'partial')
    .reduce(
      (sum, o) => sum + o.items.filter((i) => !i.isCartOption).reduce((s, i) => s + i.qte, 0),
      0,
    );

  // DF-0233 — combien de ces plats sont DEJA sortis en cuisine. « Partir »
  // efface la table sans faire la difference entre une table vide et une table
  // preparee et servie : mesure prod du 2026-09-13 chez Yuji, 32 annulations
  // sur 32 portaient un bon de cuisine deja sorti, 21 d'entre elles plus de
  // 20 min apres l'envoi (jusqu'a 10 h), pour 736,70 € en 7 jours. Le serveur
  // lisait « Le client part ? » dans les deux cas.
  const sentToKitchenCount = (sessionDetail?.orders ?? [])
    .filter(
      (o) =>
        o.onlineStatus !== 'cancelled' && o.paymentStatus !== 'paid' && o.paymentStatus !== 'partial',
    )
    .reduce(
      (sum, o) =>
        sum +
        o.items
          .filter((i) => !i.isCartOption && i.sentToKitchen)
          .reduce((s, i) => s + i.qte, 0),
      0,
    );

  // DF-0319 — plats retirés par le « − » après l'envoi en cuisine, commandes
  // annulées comprises : vider la dernière ligne annule la commande, qui sort
  // donc de `sentToKitchenCount`. Sans ce compte, une table servie puis vidée
  // ligne à ligne ressemblait au ✔ à une note jamais remplie et se fermait sans
  // un mot — le geste était devenu le rituel de fermeture chez Yuji (52 tables
  // en 21 jours, 2 452,90 €, médiane 2 s entre le dernier « − » et la
  // fermeture).
  const removedLineCount = (sessionDetail?.orders ?? []).reduce(
    (sum, o) => sum + (o.removedQty ?? 0),
    0,
  );

  // DF-0349 — la feuille du ✔ dit ce qu'elle envoie. Chez Yuji le 2026-10-01,
  // un serveur qui encaissait la table 20 a touché la 23 voisine et l'a envoyée
  // en caisse 3 s après l'avoir ouverte : la feuille ne disait que « Envoyer en
  // caisse ? », sans table ni montant. Mêmes commandes que le transfert (toutes
  // sauf les annulées). Le montant suit le geste : avec une caisse, la somme des
  // commandes, exactement ce que le transfert emporte (`build_handoff_snapshot`)
  // — plus le forfait d'une table buffet là où le restaurant l'envoie en caisse
  // (DF-0350) : le serveur le dit (`caisseTransferTotal`), la somme des
  // commandes reste le repli quand il ne le sait pas ; sans caisse, le total
  // de l'onglet Commandes.
  const sheetOrders = (sessionDetail?.orders ?? []).filter((o) => o.onlineStatus !== 'cancelled');
  const sheetDishCount = sheetOrders.reduce(
    (sum, o) => sum + o.items.filter((i) => !i.isCartOption).reduce((s, i) => s + i.qte, 0),
    0,
  );
  const sheetAmount = formatPrice(
    capabilities?.canSendToCaisse
      ? sessionDetail?.caisseTransferTotal != null
        ? Math.round(parseFloat(sessionDetail.caisseTransferTotal) * 100)
        : sheetOrders.reduce((sum, o) => sum + Math.round(parseFloat(o.totalTtc) * 100), 0)
      : Math.round(parseFloat(sessionDetail?.runningTotal ?? '0') * 100),
  );

  function handlePrintTap() {
    if (footerBusy) return;
    if (pendingRoundCount > 0) {
      setPrintPendingOpen(true);
      return;
    }
    void handlePrint();
  }

  async function handlePrint() {
    if (footerBusy) return;
    setFooterBusy(true);
    try {
      const queued = await printSessionTotal({ sessionUuid, token }).unwrap();
      const destination = printOutcomeDestination(queued.destination);
      if (!destination || !queued.dispatchUuid) {
        showToast(t('waiter.overview.printTotalDone'));
      } else {
        showToast(t(PRINT_OUTCOME_MESSAGES[destination].sent));
        await reportPrintOutcome(queued.dispatchUuid, destination, {
          fetchStatus: (dispatchUuid) => fetchPrintTotalStatus({ dispatchUuid, token }).unwrap(),
          announce: showToast,
          t,
        });
      }
    } catch (err) {
      const payload = err as { status?: number | string; data?: { code?: string } };
      if (payload?.status === 404) {
        showToast(t('waiter.ticket.footer.printEmpty'));
      } else if (payload?.data?.code === 'SELECT_CAISSE') {
        showToast(t('waiter.overview.caisseSelectError'));
      } else {
        showToast(t('waiter.ticket.errors.generic'));
      }
    } finally {
      setFooterBusy(false);
    }
  }

  async function performClose() {
    setFooterBusy(true);
    try {
      await closeSession({ sessionUuid, token }).unwrap();
      onFinished();
    } catch {
      showToast(t('waiter.ticket.errors.generic'));
    } finally {
      setFooterBusy(false);
    }
  }

  function handleFinishTap() {
    if (footerBusy) return;
    if (!hasSessionLines) {
      // DF-0255 — « aucune ligne envoyée » n'a jamais voulu dire « rien à
      // perdre ». W-T-82 lisait `hasSessionLines` seul, donc une table dont
      // le panier n'est PAS encore parti tombait dans le cas « note vide » :
      // le ✔ la fermait sans un mot et `handleFinished` jetait le panier,
      // que la sauvegarde localStorage ne rattrape pas (elle ne garde que
      // les lignes reprises d'un client, DF-0224). Chez Yuji le 2026-09-18
      // c'est arrivé deux fois de suite sur la même tablée de 31 couverts :
      // 80 articles, ~927 €, saisis puis perdus, 10 s puis 16 s après un
      // envoi refusé — le ✔ vert est exactement là où le serveur va vérifier
      // si sa tournée est partie. « Partir » gardait déjà cette garde
      // (W-T-85) ; le ✔ passe par la même, avec sa propre continuation.
      // DF-0319 — une table servie puis vidée n'est pas une note vide : elle
      // passe par la confirmation de « Partir », qui dit combien de plats ont
      // été retirés, au lieu de fermer en silence.
      const closeEmptyNote = () => {
        if (removedLineCount > 0) {
          setLeaveError(null);
          setLeaveConfirmOpen(true);
          return;
        }
        void performClose(); // W-T-82 — empty note: no sheet at all.
      };
      if (hasUnsentCart) {
        onRequestCartGuard(closeEmptyNote);
        return;
      }
      closeEmptyNote();
    } else {
      setFinishSheetError(null);
      setFinishSheetOpen(true);
    }
  }

  async function handleSendToCaisse() {
    setFooterBusy(true);
    setFinishSheetError(null);
    try {
      await handoffSessionToCaisse({ sessionUuid, token }).unwrap();
      // SC-947 — la réponse ne dit que « mis en file » : la caisse peut encore
      // refuser l'import quelques secondes plus tard. Fermer ici libérait le
      // couvert et faisait disparaître la tournée du mobile serveur alors que
      // la caisse n'avait rien reçu — perte de commande pure. La clôture est
      // désormais faite côté serveur sur l'accusé `imported` de la caisse,
      // seul point que tous les émetteurs traversent (comme SC-946).
      showToast(t('waiter.ticket.footer.finishSheet.sentToCaisse'));
      setFinishSheetOpen(false);
      onFinished();
    } catch (err) {
      const payload = err as { status?: number | string; data?: { code?: string; detail?: unknown } };
      const code = payload?.data?.code;
      if (code === 'SELECT_CAISSE') {
        setFinishSheetError(t('waiter.overview.caisseSelectError'));
      } else if (code === 'HELD_ROUND_EXISTS') {
        setFinishSheetOpen(false);
        showToast(t('waiter.ticket.errors.heldRoundExists'));
        onShowCommandes?.();
      } else if (payload?.status === 409 || code === 'ALREADY_REQUESTED') {
        setFinishSheetError(t('waiter.overview.caisseAlreadySentError'));
      } else if (typeof payload?.data?.detail === 'string') {
        setFinishSheetError(payload.data.detail);
      } else {
        setFinishSheetError(t('waiter.ticket.errors.generic'));
      }
    } finally {
      setFooterBusy(false);
    }
  }

  async function handleFinishDirect() {
    setFooterBusy(true);
    setFinishSheetError(null);
    try {
      await finishSession({ sessionUuid, token }).unwrap();
      setFinishSheetOpen(false);
      onFinished();
    } catch (err) {
      const code = (err as { data?: { code?: string } } | undefined)?.data?.code;
      if (code === 'PENDING_ROUND_EXISTS') {
        showToast(t('waiter.ticket.errors.pendingRoundExists'));
        setFinishSheetOpen(false); // stays on Commandes — no navigation.
      } else if (code === 'ORDER_LOCKED') {
        showToast(t('waiter.ticket.errors.orderLocked'));
        setFinishSheetOpen(false);
      } else if (code === 'HELD_ROUND_EXISTS') {
        showToast(t('waiter.ticket.errors.heldRoundExists'));
        setFinishSheetOpen(false);
        onShowCommandes?.();
      } else {
        setFinishSheetError(t('waiter.ticket.errors.generic'));
      }
    } finally {
      setFooterBusy(false);
    }
  }

  function handleLeaveTap() {
    if (footerBusy) return;
    if (hasUnsentCart) {
      onRequestCartGuard(() => {
        setLeaveError(null);
        setLeaveConfirmOpen(true);
      });
    } else {
      setLeaveError(null);
      setLeaveConfirmOpen(true);
    }
  }

  async function handleLeaveConfirm() {
    setFooterBusy(true);
    setLeaveError(null);
    try {
      if (hasSessionLines) {
        await cancelSession({ sessionUuid, token }).unwrap();
      } else {
        await closeSession({ sessionUuid, token }).unwrap();
      }
      setLeaveConfirmOpen(false);
      onFinished();
    } catch (err) {
      const code = (err as { data?: { code?: string } } | undefined)?.data?.code;
      setLeaveError(
        code === 'CAISSE_HANDOFF_ACTIVE' ? t('waiter.ticket.errors.caisseHandoffActive') : t('waiter.ticket.errors.generic'),
      );
    } finally {
      setFooterBusy(false);
    }
  }

  return (
    <div
      data-testid="commandes-footer"
      className="shrink-0 grid grid-cols-[auto_1fr_minmax(0,auto)_auto] items-center gap-2 min-[360px]:gap-3 px-3 py-2 border-t-2 border-hairline bg-paper"
      style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}
    >
      <button
        type="button"
        data-testid="footer-leave"
        disabled={footerBusy}
        onClick={handleLeaveTap}
        className={`${withKitchenReprint ? 'px-2 min-[360px]:px-3 min-[360px]:min-w-[84px] min-[400px]:min-w-[104px]' : 'px-3 min-[360px]:min-w-[104px]'} h-12 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40`}
        style={{ backgroundColor: 'var(--w-danger)' }}
      >
        {t('waiter.ticket.footer.leave')}
      </button>
      <span />
      {/* SC-1038 — « 确保打印厨房纸在 Imprimer 旁边 » : les deux papiers se
          demandent au même endroit. La paire occupe la cellule qu'occupait
          « Imprimer » seul, donc sans le bouton cuisine la grille ne bouge
          pas. Il est à GAUCHE de « Imprimer » : entre « Imprimer » et le ✔ un
          appui manqué terminerait la table. La colonne est `minmax(0,auto)` et
          « Imprimer » sait se tronquer : sous une police de repli plus large
          que Fira Sans (Google Fonts qui ne répond pas), c'est son libellé
          qui cède et jamais le ✔ qui sort de l'écran (SC-761). */}
      {(withKitchenReprint || capabilities?.canPrintTotal) && (
        <div className="flex items-center gap-2">
          {withKitchenReprint && (
            <button
              type="button"
              data-testid="footer-reprint-kitchen"
              aria-label={t('waiter.ticket.commandes.reprintKitchen.aria')}
              disabled={footerBusy}
              onClick={() => setReprintSheetOpen(true)}
              className="shrink-0 w-12 h-12 flex items-center justify-center border-2 rounded-btn disabled:opacity-40"
              style={{ borderColor: 'var(--w-info)', color: 'var(--w-info)' }}
            >
              <KitchenIcon />
            </button>
          )}
          {capabilities?.canPrintTotal && (
            <button
              type="button"
              data-testid="footer-print"
              disabled={footerBusy}
              onClick={handlePrintTap}
              className={`${withKitchenReprint ? 'min-w-0 truncate px-2 min-[360px]:px-3 min-[360px]:min-w-[104px] min-[400px]:min-w-[120px]' : 'px-3 min-[360px]:min-w-[120px]'} h-12 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40`}
              style={{ backgroundColor: 'var(--w-info)' }}
            >
              {t('waiter.ticket.footer.print')}
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        data-testid="footer-finish"
        aria-label={t('waiter.ticket.footer.finishAria')}
        disabled={footerBusy}
        onClick={handleFinishTap}
        className="w-[52px] h-[52px] rounded-full flex items-center justify-center text-white text-[28px] disabled:opacity-40"
        style={{ backgroundColor: 'var(--w-success)' }}
      >
        ✔
      </button>

      {finishSheetOpen && (
        <div
          data-testid="finish-sheet"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[1190] flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--w-scrim)' }}
          onClick={onFinishSheetScrim}
        >
          <div className="w-full max-w-[420px] bg-paper rounded-2xl p-5 flex flex-col gap-3">
            {/* DF-0349 — la feuille existe pour rattraper une mauvaise table : ce
                qu'elle envoie passe en premier et en gros (la taille du numéro
                d'une tuile), puis son contenu, puis la question. */}
            <div className="flex items-start justify-between gap-2">
              <p data-testid="finish-sheet-label" className="min-w-0 font-sans text-[28px] font-bold leading-tight break-words">
                {kind === 'dine_in' ? t('waiter.ticket.header.tableLabel', { table: label }) : label}
              </p>
              <button
                type="button"
                data-testid="finish-close"
                aria-label={t('common.close')}
                disabled={footerBusy}
                onClick={() => setFinishSheetOpen(false)}
                className="-me-2 -mt-2 w-11 h-11 shrink-0 flex items-center justify-center font-sans text-2xl leading-none text-ink disabled:opacity-40"
              >
                ×
              </button>
            </div>
            <p data-testid="finish-sheet-summary" className="font-mono text-[18px] font-bold tabular-nums">
              {sheetDishCount === 0
                ? sheetAmount
                : t(
                    sheetDishCount === 1
                      ? 'waiter.ticket.footer.finishSheet.summaryOne'
                      : 'waiter.ticket.footer.finishSheet.summaryOther',
                    { n: sheetDishCount, amount: sheetAmount },
                  )}
            </p>
            <p className="font-sans text-lg font-bold">
              {capabilities?.canSendToCaisse
                ? t('waiter.ticket.footer.finishSheet.titleCaisse')
                : t('waiter.ticket.footer.finishSheet.titleDirect')}
            </p>
            {/* Ce que le geste entraîne, dit avant le geste : la commande quitte
                le carnet et ne se reprend pas d'ici. */}
            <p data-testid="finish-sheet-body" className="font-sans text-[16px]">
              {capabilities?.canSendToCaisse
                ? t('waiter.ticket.footer.finishSheet.bodyCaisse')
                : t('waiter.ticket.footer.finishSheet.bodyDirect')}
            </p>
            {finishSheetError && (
              <p role="alert" className="font-mono text-xs" style={{ color: 'var(--w-danger)' }}>
                {finishSheetError}
              </p>
            )}
            {/* SC-1457 — restaurant à l'ancienne caisse : le ✔ ouvre le pont sur
                cette addition, qui l'envoie puis la valide (« Confirmer »). Un
                lien, pas un appel : c'est le navigateur qui parle à la caisse,
                depuis la page du pont. Le carnet quitte la note comme après
                « Envoyer en caisse » vers une caisse liée : la liste, qui se
                relit seule, la retire dès que le pont l'a validée — et la
                garde si l'envoi échoue. */}
            {bridgeLink && (
              <a
                data-testid="finish-bridge"
                href={bridgeLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  setFinishSheetOpen(false);
                  onFinished();
                }}
                className="min-h-12 px-3 py-1 flex items-center justify-center leading-tight rounded-btn font-sans text-[18px] font-bold text-white"
                style={{ backgroundColor: 'var(--w-info)' }}
              >
                {t('waiter.ticket.footer.finishSheet.caisse')}
              </a>
            )}
            {/* Annuler à gauche en rouge, le geste à droite en vert : l'ordre
                habituel d'une boîte de confirmation (inversé en arabe par le
                sens de lecture). La carte est centrée à l'écran, loin du ✔ du
                pied : un second appui sur le ✔ ne tombe sur aucun des deux. */}
            <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-3">
              <button
                type="button"
                data-testid="finish-cancel"
                disabled={footerBusy}
                onClick={() => setFinishSheetOpen(false)}
                className="min-h-12 px-4 min-w-[96px] rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40"
                style={{ backgroundColor: 'var(--w-danger)' }}
              >
                {t('waiter.ticket.footer.finishSheet.cancel')}
              </button>
              {/* SC-1006 — avec une caisse liée, la table part en caisse : plus de
                  « Terminer sans caisse » ici. Sans caisse, « Terminer » reste le seul geste. */}
              {capabilities?.canSendToCaisse ? (
                <button
                  type="button"
                  data-testid="finish-caisse"
                  disabled={footerBusy}
                  onClick={() => void handleSendToCaisse()}
                  className="min-h-12 px-3 py-1 leading-tight rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40"
                  style={{ backgroundColor: 'var(--w-success)' }}
                >
                  {t('waiter.ticket.footer.finishSheet.caisse')}
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="finish-no-caisse"
                  disabled={footerBusy}
                  onClick={() => void handleFinishDirect()}
                  className="min-h-12 px-3 py-1 leading-tight rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40"
                  style={{ backgroundColor: 'var(--w-success)' }}
                >
                  {t('waiter.ticket.footer.finishSheet.directOnly')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {reprintSheetOpen && (
        <KitchenReprintSheet
          sessionUuid={sessionUuid}
          token={token}
          onClose={() => setReprintSheetOpen(false)}
          showToast={showToast}
        />
      )}

      {printPendingOpen && (
        <div
          data-testid="print-pending-confirm"
          role="alertdialog"
          aria-modal="true"
          className="fixed inset-0 z-[1190] flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--w-scrim)' }}
        >
          <div className="bg-paper border-2 border-ink rounded-btn p-5 max-w-sm md:max-w-[420px] w-full flex flex-col gap-4">
            <p className="font-sans text-lg font-bold">
              {t('waiter.ticket.footer.printPending.title', { n: pendingRoundCount })}
            </p>
            <p className="font-sans text-[18px]">{t('waiter.ticket.footer.printPending.body')}</p>
            <button
              type="button"
              data-testid="print-pending-review"
              onClick={() => {
                setPrintPendingOpen(false);
                onShowCommandes?.();
              }}
              className="h-12 rounded-btn font-sans text-[18px] font-bold text-white"
              style={{ backgroundColor: 'var(--w-success)' }}
            >
              {t('waiter.ticket.footer.printPending.review')}
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                data-testid="print-pending-cancel"
                disabled={footerBusy}
                onClick={() => setPrintPendingOpen(false)}
                className="h-12 flex-1 border-2 border-ink rounded-btn font-sans text-[18px] font-bold disabled:opacity-40"
              >
                {t('waiter.ticket.footer.printPending.cancel')}
              </button>
              <button
                type="button"
                data-testid="print-pending-anyway"
                disabled={footerBusy}
                onClick={() => {
                  setPrintPendingOpen(false);
                  void handlePrint();
                }}
                className="h-12 flex-1 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40"
                style={{ backgroundColor: 'var(--w-info)' }}
              >
                {t('waiter.ticket.footer.printPending.anyway')}
              </button>
            </div>
          </div>
        </div>
      )}

      {leaveConfirmOpen && (
        <div
          data-testid="leave-confirm"
          role="alertdialog"
          aria-modal="true"
          className="fixed inset-0 z-[1190] flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--w-scrim)' }}
          onClick={onLeaveConfirmScrim}
        >
          <div className="bg-paper border-2 border-ink rounded-btn p-5 max-w-sm md:max-w-[420px] w-full flex flex-col gap-4">
            <p className="font-sans text-lg font-bold">{t('waiter.ticket.footer.leaveConfirm.title')}</p>
            <p className="font-sans text-[18px]">
              {!hasSessionLines
                ? t('waiter.ticket.footer.leaveConfirm.emptyBody')
                : unpaidLineCount === 0
                  ? // §2.7 — a session whose only remaining lines are
                    // `isCartOption` question lines (all dishes already
                    // decremented) still HAS lines, but the count is 0: the
                    // count-free copy avoids "0 plats non payés".
                    t('waiter.ticket.footer.leaveConfirm.withLinesBody')
                  : unpaidLineCount === 1
                    ? t('waiter.ticket.footer.leaveConfirm.withLinesBodyOne', { n: unpaidLineCount })
                    : t('waiter.ticket.footer.leaveConfirm.withLinesBodyOther', { n: unpaidLineCount })}
            </p>
            {sentToKitchenCount > 0 && (
              <p className="font-sans text-[18px] font-bold" data-testid="leave-confirm-kitchen">
                {sentToKitchenCount === 1
                  ? t('waiter.ticket.footer.leaveConfirm.kitchenWarnOne', { n: sentToKitchenCount })
                  : t('waiter.ticket.footer.leaveConfirm.kitchenWarnOther', {
                      n: sentToKitchenCount,
                    })}
              </p>
            )}
            {removedLineCount > 0 && (
              <p className="font-sans text-[18px] font-bold" data-testid="leave-confirm-removed">
                {removedLineCount === 1
                  ? t('waiter.ticket.footer.leaveConfirm.removedWarnOne', { n: removedLineCount })
                  : t('waiter.ticket.footer.leaveConfirm.removedWarnOther', {
                      n: removedLineCount,
                    })}
              </p>
            )}
            {leaveError && (
              <p role="alert" className="font-mono text-xs" style={{ color: 'var(--w-danger)' }}>
                {leaveError}
              </p>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                data-testid="leave-confirm-cancel"
                disabled={footerBusy}
                onClick={() => setLeaveConfirmOpen(false)}
                className="h-12 flex-1 border-2 border-ink rounded-btn font-sans text-[18px] font-bold disabled:opacity-40"
              >
                {t('waiter.ticket.footer.leaveConfirm.cancel')}
              </button>
              <button
                type="button"
                data-testid="leave-confirm-submit"
                disabled={footerBusy}
                onClick={() => void handleLeaveConfirm()}
                className="h-12 flex-1 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40"
                style={{ backgroundColor: 'var(--w-danger)' }}
              >
                {t('waiter.ticket.footer.leaveConfirm.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
